import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LegendProps,
  Customized,
} from "recharts";
import { CustomTooltip } from "@/features/dashboard/features/markup-trend-section/components/custom-tooltip";
import { useSearchParams } from "react-router-dom";
import { useGetMarkupTrend } from "@/features/dashboard/features/markup-trend-section/hooks/useGetMarkupTrend";
import CustomLegend from "@/features/dashboard/features/markup-trend-section/components/custom-legend";
import useIdentity from "@/features/ui/hooks/use-identity";
import useListAllCompanies, {
  ListAllCompaniesPathParameter,
  ListAllCompaniesRequest,
} from "@/features/line-sheet-sets/hooks/use-list-all-companies";
import { useRecoilValue } from "recoil";
import { NoDataOverlay } from "@/features/dashboard/features/markup-trend-section/components/no-results-chart";
import { SEASON_LIST } from "@/features/dashboard/features/markup-trend-section/utils/constants";
import { markupSectionStore } from "@/features/dashboard/features/markup-trend-section/store/markup-section-store";
import { removeObjDuplicateBy } from "@/features/dashboard/utils";
import debounce from "lodash/debounce";
import { useGetMarkupTrendBrandList } from "@/features/dashboard/features/markup-trend-section/hooks/useGetMarkupTrendBrandList";
import useGetCompanies from "@/features/order-sheet-sets/hooks/useGetCompanies";
import { EmptyChart } from "@/features/dashboard/features/markup-trend-section/components/empty-chart";

type LegendPayload = NonNullable<LegendProps["payload"]>[number];

const colors = [
  { lineColor: "#48B5FE", legendBgColor: "#F0F9FF", legendTxtColor: "#0074CC" },
  { lineColor: "#45D0C2", legendBgColor: "#EFFBFA", legendTxtColor: "#1A9387" },
  { lineColor: "#A056D2", legendBgColor: "#F8F3FC", legendTxtColor: "#8B35C5" },
  { lineColor: "#E64C4C", legendBgColor: "#FFEEEE", legendTxtColor: "#CC1D1D" },
  { lineColor: "#FFD600", legendBgColor: "#FFF7CC", legendTxtColor: "#B27700" },
  { lineColor: "#744AFC", legendBgColor: "#F3F0FF", legendTxtColor: "#5818FB" },
  { lineColor: "#FF1FC7", legendBgColor: "#FFF0FB", legendTxtColor: "#C70095" },
  { lineColor: "#FC7019", legendBgColor: "#FFF2EB", legendTxtColor: "#BD4800" },
];

function MarkupTrendChart() {
  const markupSectionType = useRecoilValue(markupSectionStore);
  const [searchParams, setSearchParams] = useSearchParams();
  const [outerOpen, setOuterOpen] = useState(false);

  const data = useGetCompanies(["BRAND"]);

  // Legend 클릭 핸들러:
  // 클릭된 series(dataKey)를 토글(on/off)
  const handleLegendClick = useCallback(
    (payload: LegendPayload) => {
      const removeId = data?.find(
        (item: any) =>
          item.name === (payload.dataKey as string).replace("_solid", "")
      )?.id;

      // 2) 기존 searchParams 복제
      const params = new URLSearchParams(searchParams);

      // 3) brand_ids 중에서 removedId만 걸러내고
      const remaining = params
        .getAll("brand_ids")
        .filter((id) => id !== String(removeId));

      // 4) brand_ids 파라미터 전부 삭제
      params.delete("brand_ids");

      // 5) 남은 값들을 다시 append
      remaining.forEach((id) => params.append("brand_ids", id));

      // 6) URL 업데이트 (replace 옵션은 뒤로가기도 깔끔하게)
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams, data]
  );

  const { markupTrendData } = useGetMarkupTrend();

  const stockData = React.useMemo(() => {
    if (!markupTrendData || markupTrendData.length === 0) return [];
    return markupTrendData
      .filter((d: any) => d.line_sheet_set_type === "STOCK")
      .map(({ line_sheet_set_type, ...rest }: any) => rest);
  }, [markupTrendData]);

  const preData = React.useMemo(() => {
    if (!markupTrendData || markupTrendData.length === 0) return [];
    return markupTrendData
      .filter((d: any) => d.line_sheet_set_type === "SEASON")
      .map(({ line_sheet_set_type, ...rest }: any) => rest);
  }, [markupTrendData]);

  const currentData =
    markupSectionType === "STOCK"
      ? removeObjDuplicateBy(stockData, "season")
      : preData;

  const brandIds = useMemo(
    () => searchParams.getAll("brand_ids").map(Number),
    [searchParams]
  );

  const seriesKeys = useMemo(
    () => brandIds.map((id) => data?.find((item: any) => item.id === id)?.name),
    [brandIds, data]
  );

  const completeData = useMemo(() => {
    // 1) 전체 브랜드 키 뽑기
    // if (!currentData || currentData.length === 0) return [];
    const seen = new Set();
    const refinedCurrentData = currentData.filter((item: any) => {
      if (seen.has(item.season)) return false;
      seen.add(item.season);
      return true;
    });

    const brandKeys: string[] = Array.from(
      new Set(
        refinedCurrentData.flatMap((item: any) =>
          Object.keys(item).filter((k) => k !== "season")
        )
      )
    );

    // 2) season → 원본 객체 맵으로 변환
    const dataMap = Object.fromEntries(
      refinedCurrentData.map((item: any) => [item.season, item])
    );

    // 3) SEASON_LIST 기준으로 모든 시즌을 돌면서
    return SEASON_LIST.map((season) => {
      const src = dataMap[season];
      // 기본 필드 (season, 필요 시 line_sheet_set_type)
      const row: Record<string, number | string | null> = { season };

      // 4) 각 브랜드 키에 대해 값이 있으면 parseFloat, 없으면 0
      seriesKeys.forEach((brand) => {
        row[`${brand}_solid`] =
          src && brand && src[brand] ? parseFloat(src[brand] as string) : null;
        row[`${brand}_dash`] =
          src && brand && src[brand] ? parseFloat(src[brand] as string) : 0;
      });

      return row;
    });
  }, [currentData, seriesKeys]);

  const maxTick = Math.ceil(50 / 10) * 10;
  const yTicks = Array.from({ length: maxTick / 10 + 1 }, (_, i) => i * 10);

  const [height, setHeight] = useState(0);
  useEffect(() => {
    const resizeHandler = () => {
      setHeight(window.innerHeight * 0.4); // 예시: 전체 높이의 40%로 설정
    };

    const debouncedResizeHandler = debounce(resizeHandler, 200);
    window.addEventListener("resize", debouncedResizeHandler);
    return () => {
      window.removeEventListener("resize", debouncedResizeHandler);
    };
  }, []);

  return (
    <ResponsiveContainer width="100%" height={"100%"} key={height}>
      <LineChart
        data={completeData}
        margin={{ top: 20, right: 0, left: -20, bottom: 0 }}
        style={{
          width: "97%",
        }}
      >
        {/* 그리드 점선 */}
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        {/* X축: season */}
        <XAxis
          dataKey="season"
          tickMargin={10}
          ticks={SEASON_LIST}
          type="category"
          interval={0}
        />
        {/* Y축: 0 ~ 50 고정 */}
        <YAxis domain={[0, 50]} ticks={yTicks} tickMargin={10} />
        {/* 툴팁 */}
        <Tooltip content={CustomTooltip} filterNull={false} />
        {/* 범례(클릭 제어) */}
        <Legend
          onClick={handleLegendClick}
          content={(props) => (
            <CustomLegend
              colors={colors}
              outerOpen={outerOpen}
              setOuterOpen={setOuterOpen}
              {...props}
            />
          )}
          layout={"vertical"}
          align={"right"}
          verticalAlign={"top"}
        />
        {seriesKeys?.map((key: any, index: number) => (
          <Fragment key={key + index}>
            <Line
              dataKey={`${key}_solid`}
              type="linear"
              stroke={colors[index].lineColor}
              strokeWidth={2.5}
              dot={true}
              activeDot={{ r: 6 }}
              animationDuration={1000}
            />
            <Line
              // data={completeData2}
              dataKey={`${key}_dash`}
              stroke={colors[index].lineColor}
              strokeWidth={2}
              strokeDasharray="3 3"
              type="linear"
              dot={false}
              animationDuration={1000}
            />
          </Fragment>
        ))}
        {brandIds && brandIds.length === 0 && (
          //@ts-ignore
          <Customized component={EmptyChart} />
        )}
        {currentData.length === 0 && brandIds.length > 0 && (
          //@ts-ignore
          <Customized component={NoDataOverlay} />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
}

export const MemoizedMarkupTrendChart = React.memo(MarkupTrendChart);
